import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';
// @ts-ignore
import useAuthorizedModules from '../../hooks/useAuthorizedModules';
import logo from '../../assets/Logo CLU I White.png';
// @ts-ignore
import AuthService from '../../services/auth.service';
import OrganizationSelector from '../shared/OrganizationSelector';
import NotificationBell from '../shared/NotificationBell';
import LanguageSelector from '../shared/LanguageSelector';
import { JobCenterProvider } from '../../features/jobsV5/JobCenterContext';
import { JobCenterBell } from '../../features/jobsV5/JobCenterBell';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import UserService from '../../services/user.service';
import BypassOnboardingModal from '../admin/BypassOnboardingModal';
// @ts-ignore
import PlatformNoticeBar from '../common/PlatformNoticeBar';
// @ts-ignore
import ConnectionStatusBanner from '../common/ConnectionStatusBanner';
import ImpersonationBanner from '../common/ImpersonationBanner';

interface MenuItem {
  path: string;
  name: string;
  icon: string;
  requiredRole?: string;
  moduleCode?: string;
  isDemo?: boolean;
  nameKey?: string;
}

// Interfaz para los módulos que vienen del hook useAuthorizedModules
interface Module {
  id: number;
  name: string;
  code: string;
  description?: string;
  is_basic?: boolean;
  isExtraModule?: boolean;
  originalCode?: string;
}

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches ||
    /iPhone|iPad|iPod|Android|Mobile|Tablet/i.test(navigator.userAgent));

const Layout: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [showMobileReturn, setShowMobileReturn] = useState(false);

  useEffect(() => {
    setShowMobileReturn(isTouchDevice());
  }, []);

  const isTabletOrMobileViewport = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 1024px)').matches;
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => !isTabletOrMobileViewport());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const location = useLocation();
  const { currentUser, userInfo, logout, role, loading, activeOrganization, isImpersonating, impersonatedEmail } = useAuth();
  
  // Debug: Log cuando role cambia
  useEffect(() => {
    console.log(`[Layout] role cambió a: ${role}, loading: ${loading}`);
  }, [role, loading]);
  // @ts-ignore // Evitar errores de tipado con los módulos personalizados
  const { isModuleAuthorized, authorizedModules } = useAuthorizedModules();
  // Explicitly typed the authorizedModules for TypeScript
  const typedModules = authorizedModules as Module[];
  const userMenuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(null);

  // OPTIMIZATION: Stabilize email using useMemo to prevent unnecessary useEffect executions
  // This ensures useEffect only runs when the email actually changes, not on every render
  const userEmail = useMemo(() => {
    if (isImpersonating && impersonatedEmail) return impersonatedEmail;
    return userInfo?.user?.email || currentUser?.email || null;
  }, [isImpersonating, impersonatedEmail, userInfo?.user?.email, currentUser?.email]);

  // Cerrar el menú cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current && 
        buttonRef.current && 
        !userMenuRef.current.contains(event.target as Node) && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-collapse sidebar on mobile/tablet viewports.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    if (mediaQuery.matches) {
      setIsSidebarOpen(false);
    }

    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsSidebarOpen(false);
      }
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, [isTabletOrMobileViewport]);

  // Cargar avatar del usuario para la miniatura del header
  // OPTIMIZATION: Now uses stabilized userEmail to prevent unnecessary calls
  useEffect(() => {
    const loadAvatar = async () => {
      if (!userEmail) {
        setProfileAvatarUrl(null);
        return;
      }
      
      try {
        // Cache is now handled in UserService.getAvatarObjectUrl()
        const url = await UserService.getAvatarObjectUrl(userEmail);
        setProfileAvatarUrl(url);
      } catch (error) {
        console.warn('[Layout] No se pudo cargar avatar para miniatura de usuario:', error);
        setProfileAvatarUrl(null);
      }
    };

    loadAvatar();
  }, [userEmail]); // Only depends on stabilized email

  // Cargar nombre "Full Name" desde el backend (tabla users) para usarlo en el header
  // OPTIMIZATION: Now uses stabilized userEmail to prevent unnecessary calls
  useEffect(() => {
    const loadProfileName = async () => {
      if (!userEmail) {
        setProfileDisplayName(null);
        return;
      }
      
      try {
        // Cache is now handled in UserService.getUserByEmail()
        const backendUser = await UserService.getUserByEmail(userEmail);
        if (backendUser && backendUser.name) {
          setProfileDisplayName(backendUser.name);
        } else {
          setProfileDisplayName(null);
        }
      } catch (error) {
        console.warn('[Layout] No se pudo cargar nombre completo desde backend:', error);
        setProfileDisplayName(null);
      }
    };

    loadProfileName();
  }, [userEmail]); // Only depends on stabilized email

  const handleUserMenuClick = () => {
    setShowUserMenu(prevState => !prevState);
  };

  const handleLogout = async () => {
    console.log("Cerrando sesión...");
    try {
      await logout();
    } catch (error) {
      console.error("Error en logout desde AuthContext, intentando logout directo", error);
      // Fallback: Intentar logout directamente
      try {
        await AuthService.logout();
        window.location.href = '/login';
      } catch (fallbackError) {
        console.error("Error en logout fallback", fallbackError);
        // Último recurso: limpiar localStorage y redireccionar
        localStorage.removeItem('user');
        localStorage.removeItem('originalUser');
        window.location.href = '/login';
      }
    }
  };

  // Get display name for the current user's organization
  const getUserOrganization = () => {
    if (activeOrganization) {
      return activeOrganization.organization_name || t('layout_org_no_name');
    }
    if (loading || !userInfo?.user?.members || userInfo.user.members.length === 0) {
      return '...';
    }
    return userInfo.user.members[0]?.organization?.name || t('layout_org_none');
  };

  // Use centralized useEffectiveRole hook for consistency
  const { effectiveRole: hookEffectiveRole, isSuperUser } = useEffectiveRole();
  
  // Obtener el rol del usuario con fallback chain (wrapper para compatibilidad)
  const getUserRole = (): string => {
    // Removed excessive logging that was causing performance issues
    if (loading || !hookEffectiveRole) {
        return 'loading';
    }
    return hookEffectiveRole;
  };

  // Map role code to display name (i18n)
  const getRoleDisplayName = (roleCode: string): string => {
    if (roleCode === 'loading') return '...';
    if (roleCode === 'super_user') return t('layout_role_super_user');
    if (roleCode === 'admin') return t('layout_role_admin');
    if (roleCode === 'user') return t('layout_role_user');
    return roleCode;
  };

  // Verificar acceso basado en rol
  // IMPORTANT: Use getUserRole() to get effective role with fallback chain
  const hasRoleAccess = (requiredRole?: string): boolean => {
    if (!requiredRole) return true;
    
    const effectiveRole = getUserRole();
    
    // Super usuario tiene acceso a todo
    if (effectiveRole === 'super_user') return true;
    
    // Admin solo puede acceder a funciones de admin y user
    if (effectiveRole === 'admin' && ['admin', 'user'].includes(requiredRole)) return true;
    
    // Usuario normal solo puede acceder a funciones de user
    return requiredRole === 'user' && effectiveRole === 'user';
  };

  // Static menu items (not tied to modules - Settings, Constellation, etc.)
  const staticMenuItems: MenuItem[] = [
    { path: '/control-center', name: 'Control Center', nameKey: 'layout_control_center', icon: '🕹️', requiredRole: 'super_user' },
    { path: '/clerks-ecosystem', name: 'Clerks Ecosystem', nameKey: 'layout_clerks_ecosystem', icon: '🌐', requiredRole: 'super_user', moduleCode: 'clerks_ecosystem_' },
    { path: '/clu-ng', name: 'CLU', nameKey: 'layout_clu', icon: '🤖', requiredRole: 'user' },
    { path: '/galaxia', name: 'Constellation', nameKey: 'layout_constellation', icon: '🌌', requiredRole: 'super_user' },
    { path: '/settings', name: 'Settings', nameKey: 'layout_settings', icon: '⚙️', requiredRole: 'super_user' },
    { path: '/menu', name: 'Menu', nameKey: 'layout_menu', icon: '📋', requiredRole: 'user' }
  ];

  // Generate dynamic menu items from assigned modules
  // Only show modules with show_in_menu = true in the sidebar
  const dynamicMenuItems = useMemo(() => {
    return typedModules
      .filter(module => {
        // Only include modules that are explicitly enabled for sidebar (show_in_menu = true)
        // show_in_menu = false means "only in Menu page, not in sidebar"
        if (module.show_in_menu !== true) return false;
        // Must have a route_path configured
        if (!module.route_path) return false;
        return true;
      })
      .map(module => ({
        path: module.route_path,
        name: module.display_name || module.name,
        icon: module.icon || '📦',
        moduleCode: module.code || (module.name?.toLowerCase().replace(' ', '_')),
        menuOrder: module.menu_order || 0
      }))
      .sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0));
  }, [typedModules]);

  // Combine dynamic and static menu items
  const allMenuItems = useMemo(() => {
    return [...dynamicMenuItems, ...staticMenuItems];
  }, [dynamicMenuItems, staticMenuItems]);

  // Usar useMemo en lugar de useState + useEffect para filtrar los elementos del menú
  // Esto evita el ciclo infinito de renderizaciones
  // FIX: Added hookEffectiveRole and loading to dependencies to ensure recalculation when role changes
  const filteredMenuItems = useMemo(() => {
    // Get effective role for super_user check
    const effectiveRole = getUserRole();
    
    // DIAGNOSTIC LOGGING
    console.log('[Layout] ===== MENU FILTERING DIAGNOSTIC =====');
    console.log('[Layout] getUserRole() returned:', effectiveRole);
    console.log('[Layout] loading state:', loading);
    console.log('[Layout] hookEffectiveRole:', hookEffectiveRole);
    console.log('[Layout] role from AuthContext:', role);
    console.log('[Layout] isSuperUser:', isSuperUser);
    console.log('[Layout] allMenuItems count:', allMenuItems.length);
    console.log('[Layout] staticMenuItems:', staticMenuItems.map(m => ({ name: m.name, requiredRole: m.requiredRole })));
    console.log('[Layout] dynamicMenuItems count:', dynamicMenuItems.length);
    
    // FIX: Wait for role to load before showing menu
    if (loading || !hookEffectiveRole || effectiveRole === 'loading') {
      console.log('[Layout] Role is still loading, returning empty menu');
      return [];
    }
    
    // For non-super_user users: Only show Dashboard and Menu in sidebar
    // Super users keep full sidebar (no changes)
    // FIX: Explicitly check for 'user' and 'admin' roles, not just '!== super_user'
    if (effectiveRole === 'user' || effectiveRole === 'admin') {
      console.log(`[Layout] User is ${effectiveRole} (NOT super_user), showing simple menu`);
      const simpleMenuItems: MenuItem[] = [
        { path: '/dashboard', name: 'Dashboard', nameKey: 'layout_dashboard', icon: '📊', requiredRole: 'user' },
        { path: '/clu-ng', name: 'CLU', nameKey: 'layout_clu', icon: '🤖', requiredRole: 'user' },
        { path: '/menu', name: 'Menu', nameKey: 'layout_menu', icon: '📋', requiredRole: 'user' }
      ];
      
      const result = simpleMenuItems.map(item => ({
        ...item,
        isDemo: false
      }));
      console.log('[Layout] Simple menu items:', result.map(m => m.name));
      return result;
    }
    
    // Super user: Full sidebar (original logic - no changes)
    console.log('[Layout] User IS super_user, showing full sidebar with role filtering');
    const filtered = allMenuItems.filter(item => {
      // Super usuario tiene acceso a TODOS los módulos sin verificar organización
      // Still check role requirements (e.g., super_user required for Settings)
      const roleAuthorized = hasRoleAccess(item.requiredRole);
      console.log(`[Layout] Menu item "${item.name}" (requiredRole: ${item.requiredRole}) - authorized: ${roleAuthorized}`);
      return roleAuthorized;
    });
    console.log('[Layout] Filtered menu items for super_user:', filtered.map(m => m.name));
    
    return filtered.map(item => {
      // Verificar si el módulo es DEMO (isExtraModule)
      let isDemo = false;
      
      // Buscar el módulo en authorizedModules
      if (item.moduleCode) {
        // Usar typedModules para evitar errores de TypeScript
        const moduleInfo = typedModules.find(m => 
          m.code === item.moduleCode || 
          (m.originalCode && m.originalCode === item.moduleCode)
        );
        
        if (moduleInfo && moduleInfo.isExtraModule) {
          isDemo = true;
        }
      }
      
      // Retornar el ítem con la propiedad isDemo añadida
      return {
        ...item,
        isDemo
      };
    });
  }, [allMenuItems, role, authorizedModules, typedModules, hookEffectiveRole, loading, isSuperUser]);

  // Use centralized ControlCenterRegistry
  const [ccSummary, setCcSummary] = React.useState<{ pending: number } | null>(null);
  
  // OPTIMIZATION: Use ref to prevent duplicate intervals in React StrictMode
  const ccIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  
  React.useEffect(() => {
    let active = true;
    
    // Clear any existing interval first (React StrictMode double mount protection)
    if (ccIntervalRef.current) {
      clearInterval(ccIntervalRef.current);
      ccIntervalRef.current = null;
    }
    
    // Load initial summary
    (async () => {
      try {
        const { ControlCenterRegistry } = await import('../../stores/control-center.registry');
        const summary = await ControlCenterRegistry.getSummary();
        if (active && summary) {
          setCcSummary({ pending: summary?.commands?.pending || 0 });
        }
      } catch (e) {
        // ignore
      }
    })();
    
    // Polling: refresh every 10 seconds using the registry
    // Note: apiCall now deduplicates simultaneous requests, so multiple components won't cause duplicate backend calls
    ccIntervalRef.current = setInterval(async () => {
      try {
        const { ControlCenterRegistry } = await import('../../stores/control-center.registry');
        const summary = await ControlCenterRegistry.getSummary();
        if (active) {
          setCcSummary({ pending: summary?.commands?.pending || 0 });
        }
      } catch (e) {
        // ignore
      }
    }, 10000);
    
    return () => { 
      active = false;
      if (ccIntervalRef.current) {
        clearInterval(ccIntervalRef.current);
        ccIntervalRef.current = null;
      }
    };
  }, []);

  // Organization selector is now handled by the centralized OrganizationSelector component
  // It will show if user has at least one organization

  const isCLUPath = location.pathname.startsWith('/clu');
  const isEmbeddedMobileShell = false;

  return (
    <JobCenterProvider>
    <div className="flex flex-col h-screen bg-surface">
      <ConnectionStatusBanner />
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <div className={`${isEmbeddedMobileShell ? 'hidden' : (isSidebarOpen ? 'w-64' : 'w-20')} bg-surface border-r border-line transition-all duration-300 ease-in-out`}>
        <div className="flex items-center justify-between p-4 border-b border-line">
          <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <img src={logo} alt="GRID Logo" className="h-8 w-8" />
          </Link>
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-ink-muted hover:text-ink"
          >
            {isSidebarOpen ? '◀' : '▶'}
          </button>
        </div>
        
        <nav className="mt-4">
          {filteredMenuItems.map((item) => {
            // Handle query strings in paths
            const itemPath = item.path.split('?')[0];
            const isActive = location.pathname === itemPath || 
                            (itemPath === '/settings/clu-admin' && location.pathname.startsWith('/settings/clu-admin'));
            return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center p-4 text-ink-muted hover:bg-surface-2 hover:text-ink transition-colors
                ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 border-r-2 border-blue-600' : ''}`}
            >
              <span className="mr-3">{item.icon}</span>
              {isSidebarOpen && (
                <div className="flex items-center justify-between w-full">
                  <span className="flex items-center">
                    {item.nameKey ? t(item.nameKey) : item.name}
                    {item.path === '/control-center' && ccSummary && ccSummary.pending > 0 && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700">
                        {ccSummary.pending}
                      </span>
                    )}
                  </span>
                  {item.isDemo && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700">
                      {t('layout_demo')}
                    </span>
                  )}
                </div>
              )}
            </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className={`bg-surface border-b border-line ${isEmbeddedMobileShell ? 'p-2 sm:p-3' : 'p-4'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {showMobileReturn && (
                <button
                  onClick={() => navigate('/m/clu-ng')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 hover:dark:bg-blue-900/40 transition-colors"
                  title="Back to mobile view"
                >
                  ← Mobile
                </button>
              )}
            <h1 className="text-xl text-ink">
              {location.pathname === '/settings/llm-costs'
                ? t('app_grid_llm_costs')
                : location.pathname === '/navigator-admin' || location.pathname === '/settings/navigator-admin'
                ? t('app_navigator_admin')
                : location.pathname === '/navigator-performance' || location.pathname === '/settings/navigator-performance'
                ? t('app_navigator_performance')
                : (() => {
                    const headerItem = allMenuItems.find(item => item.path === location.pathname);
                    return headerItem ? (headerItem.nameKey ? t(headerItem.nameKey) : headerItem.name) : t('app_grid');
                  })()}
            </h1>
            </div>
            <div className="flex items-center space-x-4 relative user-menu-container" style={{ zIndex: 9999 }}>
              {/* Centralized Organization Selector */}
              <OrganizationSelector />

              {/* Language selector: EN / ES */}
              <LanguageSelector />

              {/* Notification Bell - Only show for super_user and admin */}
              {hookEffectiveRole && hookEffectiveRole !== 'user' && (
                <div className="flex items-center gap-2">
                  <NotificationBell />
                  {/* JobCenterBell is safe here because it's inside JobCenterProvider */}
                  <JobCenterBell />
                </div>
              )}

              <span className="text-ink">
                {isImpersonating ? impersonatedEmail : (profileDisplayName || userInfo?.user?.name || currentUser?.email || t('header_user'))}
                <span className={`text-xs ml-1 px-2 py-1 rounded-full text-white ${isImpersonating ? 'bg-amber-500' : 'bg-blue-600'}`}>
                  {isImpersonating ? 'Impersonated' : getRoleDisplayName(getUserRole())}
                </span>
              </span>
              <button 
                ref={buttonRef}
                className="flex items-center justify-center text-ink hover:text-ink focus:outline-none p-1 rounded-full hover:bg-surface-2 active:bg-gray-200 user-menu-button"
                onClick={handleUserMenuClick}
                aria-label={t('header_user_menu')}
                aria-expanded={showUserMenu}
                style={{ cursor: 'pointer' }}
              >
                {profileAvatarUrl ? (
                  <img
                    src={profileAvatarUrl}
                    alt="User avatar"
                    className="w-8 h-8 rounded-full object-cover border border-gray-600"
                  />
                ) : (
                  <span className="text-xl">👤</span>
                )}
              </button>
              
              {showUserMenu && (
                <div 
                  ref={userMenuRef}
                  className="absolute right-0 top-12 bg-surface border border-line rounded-lg shadow-xl z-[9999] w-64 user-menu-dropdown"
                  style={{ 
                    position: 'absolute', 
                    right: 0, 
                    top: '3rem', 
                    zIndex: 9999, 
                    pointerEvents: 'auto',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div className="p-4 border-b border-line">
                    <p className="text-ink font-medium">{isImpersonating ? impersonatedEmail : (userInfo?.user?.email || currentUser?.email || t('header_no_email'))}</p>
                    <p className="text-ink-muted text-sm mt-1">
                      {t('header_role')}: <span className={`font-medium ${isImpersonating ? 'text-amber-600 dark:text-amber-300' : 'text-blue-600 dark:text-blue-300'}`}>{isImpersonating ? 'Impersonated Session' : getRoleDisplayName(getUserRole())}</span>
                    </p>
                    <p className="text-ink-subtle text-xs mt-1">
                      {t('header_organization')}: {getUserOrganization()}
                    </p>
                  </div>
                  <Link
                    to="/profile"
                    className="w-full text-left p-3 text-ink hover:bg-surface-2 flex items-center cursor-pointer transition-colors"
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                  >
                    <span className="mr-2">👤</span> {t('header_my_profile')}
                  </Link>
                  {(hookEffectiveRole === 'admin' || isSuperUser) && (
                    <Link
                      to="/billing"
                      className="w-full text-left p-3 text-ink hover:bg-surface-2 flex items-center cursor-pointer transition-colors"
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                      onClick={() => setShowUserMenu(false)}
                    >
                      <span className="mr-2">📋</span> {t('header_my_billing')}
                    </Link>
                  )}
                  <Link
                    to="/my-wallet"
                    className="w-full text-left p-3 text-ink hover:bg-surface-2 flex items-center cursor-pointer transition-colors"
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <span className="mr-2">💼</span> {t('header_my_wallet')}
                  </Link>
                  {isSuperUser && (
                    <button
                      onClick={() => {
                        setShowBypassModal(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left p-3 text-ink hover:bg-surface-2 flex items-center cursor-pointer transition-colors border-t border-line mt-1 pt-3"
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="mr-2">⚡</span> {t('header_admin_skip_onboarding')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      localStorage.removeItem('clu_shell_v2');
                      window.location.reload();
                    }}
                    className="w-full text-left p-3 text-blue-600 dark:text-blue-300 hover:bg-blue-50 hover:dark:bg-blue-900/30 flex items-center cursor-pointer transition-colors border-t border-line mt-1 pt-3"
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="mr-2">✨</span> Back to new design
                  </button>
                  <button
                    className="w-full text-left p-3 text-ink hover:bg-surface-2 flex items-center cursor-pointer transition-colors"
                    onClick={handleLogout}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="mr-2">🚪</span> {t('header_logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* One strip, ranked server-side: arrears, due dates, missing card, trial.
            Absorbs what TrialBanner used to show on its own — see
            BillingNoticeService for why ranking does not live in the frontend. */}
        <PlatformNoticeBar />

        {/* Content Area */}
        <main className={`flex-1 overflow-x-auto overflow-y-auto ${isCLUPath ? 'bg-surface p-0 overflow-hidden' : (isEmbeddedMobileShell ? 'bg-surface p-2 sm:p-4' : 'bg-surface p-4 sm:p-6')}`}>
          <Outlet />
        </main>
      </div>
      
      </div>
      {/* Bypass Onboarding Modal */}
      <BypassOnboardingModal
        isOpen={showBypassModal}
        onClose={() => setShowBypassModal(false)}
        onSuccess={() => {
          // Optionally refresh user data or show success message
          console.log('Onboarding bypass completed successfully');
        }}
      />
    </div>
    </JobCenterProvider>
  );
};

export default Layout; 